import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  documentFailure,
  documentSuccessUpload,
  getDocumentDetails,
  setDocumentsDetails,
  getDocumentPath,
  getPathName,
  setSelectedContacts,
  setClientDocument,
} from 'redux/actions/documentActions';
import { setNewCommunication } from 'redux/actions/clientActions';
import useApi from 'hooks/useApi';
import { useTranslation } from 'react-i18next';
import { DOCUMENTS_VIEW } from 'shared/constants/variants';
import { L10n } from '@syncfusion/ej2-base';

function useDocumentation({
  refFileManager,
  setOpenFormModal,
  setOpenContactsModal,
  setShowConfirmUnPublic = () => {},
  setShowConfirmUnPublicFolder = () => {},
  setMessageError,
  onToggleError,
  setInputFile,
}) {
  const { customAreaApi } = useApi();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { documentsView } = useSelector((state) => state.document);
  const { cusNavId, mail, isCustomer, authToken } = useSelector((state) => state.auth);
  const { currentClient } = useSelector((state) => state.clientState);
  const [config, setConfig] = useState();

  const isClientView = documentsView === DOCUMENTS_VIEW.CLIENTS;
  const isRootView = documentsView === DOCUMENTS_VIEW.ROOT;

  const [, getPathItem] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading: loadingDownload }, getDownload] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading: loadingUnPublic }, unPublicDocument] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingCusConfig }, getConfig] = customAreaApi(
    {
      url: `${window.API_V1}/cus/customers/${cusNavId}`,
      method: 'GET',
    },
    { manual: true }
  );

  const loadConfig = async () => {
    const {
      data: { errors: loadingConfigErrors, data },
    } = await getConfig();
    if (loadingConfigErrors.length) {
      console.error('error getting customer config');
      return;
    }
    const { config: configResult } = data;
    setConfig(configResult);
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const getDocumentById = async (args) => {
    try {
      const {
        data: { data },
      } = await getPathItem({
        url: `${window.API_V1}/cli/items/${cusNavId}/${args.clientId}/${encodeURIComponent(mail)}/${
          args.id
        }`,
      });
      const pathIds = data.pathId.split('/');
      const clearedPathIds = pathIds.filter((x) => x.length > 0);
      clearedPathIds.splice(-1);
      let currentPath = '';
      clearedPathIds.forEach((pathName) => {
        currentPath += `${pathName}/`;
      });
      dispatch(getDocumentDetails(data));
      localStorage.setItem('documentFormState', JSON.stringify(data));
      localStorage.setItem('localizacionPath', data.pathName);
      localStorage.setItem('setPath', currentPath);
      dispatch(getDocumentPath(currentPath));
      dispatch(getPathName(data.pathName));
      if (isClientView) {
        dispatch(setClientDocument(true));
      }
      history.push('/documentation', {
        params: { documentType: 'new_word', currentDocument: data },
      });
    } catch (error) {
      console.error(`Error Documents: ${error}`);
    }
  };

  const onDownload = async (document) => {
    try {
      const { id, name } = document;
      const { data, errors } = await getDownload({
        url: `${window.API_V1}/cli/items/${cusNavId}/${document.clientId}/${encodeURIComponent(
          mail
        )}/download/${id}`,
      });
      if (data) {
        const url = `${window.REACT_APP_SERVER_URL}${window.API_V1}/cli/items/${cusNavId}/${
          document.clientId
        }/${encodeURIComponent(mail)}/download/${id}`;
        const result = await fetch(url, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const blob = await result.blob();
        const tempLink = window.document.createElement('a');
        tempLink.href = window.URL.createObjectURL(blob);
        tempLink.setAttribute('download', name);
        tempLink.click();
      }
      if (errors?.length) {
        throw new Error(
          errors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
    } catch (error) {
      setMessageError(t('custom_field_operation_error_message'));
      onToggleError();
    }
  };

  const handleMenuClick = (args) => {
    let location = '';
    let contentFolderDeleteMessage = '';
    refFileManager.current.pathNames.forEach((pathName) => {
      location += `/${pathName}`;
    });
    dispatch(getPathName(location));
    switch (args.item.id) {
      case 'file_cm_new_word':
        dispatch(getDocumentPath(refFileManager.current.path));
        if (isClientView) {
          dispatch(setClientDocument(true));
        }
        history.push('/documentation', { params: 'new_word' });
        break;
      case 'file_cm_create_notification':
        setOpenFormModal(true);
        dispatch(getDocumentDetails(args.fileDetails[0]));
        break;
      case 'file_cm_create_communication':
        if (isClientView) {
          history.push(`/clients/update/${currentClient.clientId}`);
        }
        if (isRootView) {
          history.push(`/clients/update/${currentClient.clientId}`);
        }
        dispatch(setNewCommunication('fromDocument'));
        dispatch(getDocumentDetails(args.fileDetails[0]));
        break;
      case 'file_cm_document_share_permission':
        if (args.fileDetails.length === 1) {
          dispatch(setDocumentsDetails([]));
          dispatch(getDocumentDetails(args.fileDetails[0]));
        }
        if (args.fileDetails.length > 1) {
          dispatch(getDocumentDetails(null));
          dispatch(setDocumentsDetails(args.fileDetails));
        }
        dispatch(setSelectedContacts([]));
        setOpenContactsModal(true);
        break;
      case 'file_cm_un_public_document':
        if (args.fileDetails[0].isFile) {
          setShowConfirmUnPublic(true);
        } else {
          setShowConfirmUnPublicFolder(true);
        }
        break;
      case 'file_cm_property':
        getDocumentById(args.fileDetails[0]);
        break;
      case 'file_cm_open':
        if (
          args.fileDetails[0].type !== '.png' &&
          args.fileDetails[0].type !== '.jpeg' &&
          args.fileDetails[0].type !== '.jpg' &&
          args.fileDetails[0].type !== '.ico' &&
          args.fileDetails[0].type !== ''
        ) {
          onDownload(args.fileDetails[0]);
        }
        break;
      case 'file_cm_upload':
        setInputFile(true);
        break;
      case 'file_cm_delete':
        // change the message of the delete folder when the folder under "Individuals" or "Masivas" and saveDocsComunications is enabled
        contentFolderDeleteMessage =
          (args.fileDetails[0].pathId.startsWith('1/3/4/') ||
            args.fileDetails[0].pathId.startsWith('1/3/5/')) &&
          config.saveDocsComunications
            ? t('document_content_folder_delete_on_save_enabled')
            : t('documents_content_folder_delete');

        L10n.load({
          es: {
            filemanager: {
              'Content-Folder-Delete': contentFolderDeleteMessage,
            },
          },
        });

        break;
      default:
        break;
    }
  };

  const handlePopUpClose = (args) => {
    const popupElem = document.getElementById(args.popupModule.element.id);
    if (popupElem != null) {
      dispatch(documentFailure(null));
      dispatch(documentSuccessUpload(null));
      const header = popupElem.querySelector('.e-dlg-header-content');
      if (header) header.classList.remove('e-dlg-header-alert');
      if (header) header.classList.remove('e-dlg-header-error');

      const body = popupElem.querySelector('.e-dlg-content');
      if (body) body.classList.remove('e-dlg-body-alert');
      if (body) body.classList.remove('e-dlg-body-error');

      const footer = popupElem.querySelector('.e-footer-content');
      if (footer) footer.classList.remove('e-dlg-footer-alert');
      if (footer) footer.classList.remove('e-dlg-footer-error');
      if (footer) footer.classList.remove('e-dlg-footer-create');

      const buttonUpload = popupElem.querySelector('.container-upload-attach-btn');
      if (buttonUpload) buttonUpload.remove();

      const clearH4 = popupElem.querySelector('.lf-dlg-h4');

      if (clearH4 != null) clearH4.remove();

      const informationIcon = popupElem.querySelector('.lf-icon-information');
      if (informationIcon != null) informationIcon.remove();

      const checkContainer = popupElem.querySelector('.check-container');
      if (checkContainer != null) checkContainer.remove();

      const closeIcon = popupElem.querySelector('.lf-icon-close-round');
      if (closeIcon != null) closeIcon.remove();

      const clearHr = popupElem.querySelector('.lf-dlg-hr');
      if (clearHr != null) clearHr.remove();

      const clearLabel = popupElem.querySelector('.lf-dlg-label');
      if (clearLabel != null) clearLabel.remove();
      const clearFileNameDatils = popupElem.querySelector('.file-name-document');

      if (clearFileNameDatils) clearFileNameDatils.remove();

      const clearFileStateDatils = popupElem.querySelector('.file-state-document');
      if (clearFileStateDatils) clearFileStateDatils.remove();

      const clearFileTagDatils = popupElem.querySelector('.file-tag-document');
      if (clearFileTagDatils) clearFileTagDatils.remove();

      const clearDocumentColorStateDetail = popupElem.querySelector('.document-color-state-detail');
      if (clearDocumentColorStateDetail) clearDocumentColorStateDetail.remove();
    }
  };

  const elementEmptyTable = () => {
    const contentEmpty = document.querySelector('.e-gridcontent');
    if (contentEmpty) {
      const emptyInnerContent = contentEmpty.querySelector('.e-empty-inner-content');
      if (emptyInnerContent) {
        const buttonElement = document.querySelector('.e-content-empty-btn');
        if (buttonElement) buttonElement.remove('e-content-empty-btn');
        const spanElement = document.querySelector('.e-content-empty-span');
        if (spanElement) spanElement.remove('e-content-empty-span');
        const span = document.createElement('span');
        span.innerHTML = '- o -';
        span.classList.add('e-content-empty-span');
        const button = document.createElement('button');
        button.innerHTML = t('documents_search_browser');
        button.classList.add('e-content-empty-btn');
        button.onclick = () => {
          refFileManager.current.uploadFiles();
          setInputFile(true);
        };
        emptyInnerContent.appendChild(span);
        emptyInnerContent.appendChild(button);
      }
    }
  };

  const elementEmptyLargeIcon = () => {
    const contentLargeIcons = document.querySelector('#file_largeicons');
    if (contentLargeIcons) {
      const emptyInnerContent = contentLargeIcons.querySelector('.e-empty-inner-content');
      if (emptyInnerContent) {
        const buttonLargeElement = document.querySelector('.e-content-empty-large-btn');
        if (buttonLargeElement) buttonLargeElement.remove('e-content-empty-large-btn');
        const spanElement = document.querySelector('.e-content-empty-large-span');
        if (spanElement) spanElement.remove('e-content-empty-large-span');
        const buttonLarge = document.createElement('button');
        const span = document.createElement('span');
        span.innerHTML = '- o -';
        span.classList.add('e-content-empty-large-span');
        buttonLarge.innerHTML = t('documents_search_browser');
        buttonLarge.classList.add('e-content-empty-large-btn');
        buttonLarge.onclick = () => {
          refFileManager.current.uploadFiles();
          setInputFile(true);
        };
        emptyInnerContent.appendChild(span);
        emptyInnerContent.appendChild(buttonLarge);
      }
    }
  };

  const alignToolBarItem = () => {
    if (isClientView) return 'Left';
    if (isRootView) return 'Right';
    return '';
  };

  const handleToolbarCreate = (args) => {
    for (let i = 0; i < args.items.length; i += 1) {
      const item = args.items[i];
      if (item.id === 'file_tb_refresh') {
        item.align = 'Right';
        item.text = t('general_button_refresh');
      }
      if (item.id === 'file_tb_view') {
        item.text = t('general_see');
      }
      if (item.id === 'file_tb_actions') {
        item.align = 'Right';
        item.prefixIcon = 'e-icons e-fe-tick';
        item.text = t('general_button_new');
        item.tooltipText = 'Más acciones';
      }
      if (item.id === 'file_tb_sortby') {
        item.align = 'Right';
        item.text = t('general_order_by');
      }
      if (item.id === 'file_tb_filterscli' || item.id === 'file_tb_filters') {
        item.align = 'Right';
        item.prefixIcon = 'e-icons lf-icon-filter-1';
        item.text = t('general_button_filter');
        item.tooltipText = t('general_button_filter');
      }
      if (item.id === 'file_tb_newword') {
        item.align = alignToolBarItem();
        item.prefixIcon = 'e-icons lf-icon-document';
        item.text = t('documents_new_word');
        item.tooltipText = t('documents_new_word');
      }
      if (item.id === 'file_tb_newfolder') {
        item.align = alignToolBarItem();
        item.prefixIcon = 'e-icons lf-icon-folder';
        item.text = t('documents_new_folder');
        item.tooltipText = t('documents_new_folder');
        if (!isCustomer) {
          item.disabled = true;
        }
      }
      if (item.id === 'file_tb_upload') {
        item.align = alignToolBarItem();
        item.prefixIcon = 'e-icons lf-icon-up-file';
        item.text = t('documents_upload_document');
        item.tooltipText = t('documents_upload_document');
      }
      if (item.id === 'file_tb_delete') {
        item.align = alignToolBarItem();
        item.prefixIcon = 'e-icons lf-icon-trash';
        item.text = t('general_delete');
        item.tooltipText = t('general_delete');
      }
      if (item.id === 'file_tb_cut') {
        item.align = alignToolBarItem();
        item.prefixIcon = 'e-icons lf-icon-cut';
        item.text = t('documents_cut');
        item.tooltipText = t('documents_cut');
      }
      if (item.id === 'file_tb_copy') {
        item.align = alignToolBarItem();
        item.prefixIcon = 'e-icons lf-icon-copy';
        item.text = t('documents_copy');
        item.tooltipText = t('documents_copy');
      }
      if (item.id === 'file_tb_paste') {
        item.align = alignToolBarItem();
        item.prefixIcon = 'e-icons lf-icon-paste';
        item.text = t('documents_paste');
        item.tooltipText = t('documents_paste');
      }
      if (item.id === 'file_tb_download') {
        item.align = alignToolBarItem();
        item.prefixIcon = 'e-icons lf-icon-download';
        item.text = t('documents_download');
        item.tooltipText = t('documents_download');
      }
      if (item.id === 'file_tb_rename') {
        item.align = alignToolBarItem();
        item.prefixIcon = 'e-icons lf-icon-rename';
        item.text = t('documents_rename');
        item.tooltipText = t('documents_rename');
      }
    }
  };

  const handleActionAddClick = (args) => {
    let location = '';
    refFileManager.current.pathNames.forEach((pathName) => {
      location += `/${pathName}`;
    });
    dispatch(getPathName(location));
    switch (args.item.id) {
      case 'new_folder':
        refFileManager.current.createFolder();
        break;
      case 'upload':
        refFileManager.current.uploadFiles();
        setInputFile(true);
        break;
      case 'new_word':
        dispatch(getDocumentPath(refFileManager.current.path));
        if (isClientView) {
          dispatch(setClientDocument(true));
        }
        history.push('/documentation', {
          params: { documentType: 'new_word', currentDocument: null },
        });
        break;
      default:
        break;
    }
  };

  const handleToolbarClick = (args) => {
    if (args.item.id === 'file_tb_actions') {
      localStorage.removeItem('documentFormState');
      localStorage.removeItem('localizacionPath');
      localStorage.removeItem('setPath');
      const btn = document.getElementById(args.item.id);
      if (btn !== null) {
        const rect = btn.getBoundingClientRect();
        const elem = document.getElementById('action-add-context-menu');
        if (elem !== null) {
          if (isClientView) {
            elem.ej2_instances[0].open(rect.top + 30, rect.left + 20);
          }
          if (isRootView) {
            elem.ej2_instances[0].open(rect.top + 30, rect.left + 75);
          }
          elem.ej2_instances[0].open(rect.top + 30, rect.left + 20);
          const fileUpload = document.getElementById('file_upload');
          fileUpload.webkitdirectory = false;
        }
      }
    }
    if (args.item.id === 'file_tb_filterscli') {
      const btn = document.getElementById(args.item.id);
      if (btn !== null) {
        btn.getBoundingClientRect();
        document.getElementById('file_tb_filterscli');
      }
    }
    if (args.item.id === 'file_tb_newword') {
      dispatch(getDocumentPath(refFileManager.current.path));
      if (isClientView) {
        dispatch(setClientDocument(true));
      }
      history.push('/documentation', {
        params: { documentType: 'new_word', currentDocument: null },
      });
    }
  };

  const handleTitle = (popupElem, title) => {
    const parentContent = popupElem.querySelector('.e-dlg-header-content');
    const h4 = document.createElement('h4');
    const hr = document.createElement('hr');
    if (parentContent !== null) {
      h4.innerHTML = title;
      h4.classList.add('lf-dlg-h4');
      hr.classList.add('lf-dlg-hr');
      parentContent.appendChild(h4);
      parentContent.appendChild(hr);
    }
  };

  const handleCreateFolder = (args, popupElem) => {
    if (popupElem !== null) {
      if (args.popupName === 'Delete' || args.popupName === 'Error') {
        const header = popupElem.querySelector('.e-dlg-header');
        if (header != null) {
          header.classList.add('lf-dlg-icon');
        }
      } else {
        const parent = popupElem.querySelector('.e-input-group');
        const input = popupElem.querySelector('input');
        const span = document.createElement('span');
        const label = document.createElement('label');
        if (parent !== null) {
          if (input !== null) {
            input.setAttribute('maxlength', '255');
            input.addEventListener('keyup', () => {
              span.style.display = 'block';
            });
            parent.addEventListener('mouseenter', () => {
              if (input.value !== '') {
                span.style.display = 'block';
              }
            });
            parent.addEventListener('mouseleave', () => {
              span.style.display = 'none';
            });
            if (input.id === 'newname') {
              span.style.display = 'none';
            }
            if (label != null) {
              label.innerHTML = t('general_name');
              label.classList.add('lf-dlg-label');
              const spanLabel = document.createElement('span');
              spanLabel.classList.add('lf-dlg-span');
              spanLabel.innerHTML = '*';
              label.appendChild(spanLabel);
              const labelParent = parent.parentNode;
              if (labelParent != null) {
                labelParent.prepend(label);
              }
            }
          }
        }
      }
    }
  };

  const handleAlertHeader = (popupElem) => {
    const header = popupElem.querySelector('.e-dlg-header-content');
    const div = document.createElement('div');
    header.classList.add('e-dlg-header-alert');
    div.classList.add('lf-icon-information', 'icon-information');
    header.appendChild(div);
  };

  const handleErrorHeader = (popupElem) => {
    const header = popupElem.querySelector('.e-dlg-header-content');
    const div = document.createElement('div');
    header.classList.add('e-dlg-header-error');
    div.classList.add('lf-icon-close-round', 'icon-information');
    header.appendChild(div);
  };

  const handleAlertBody = (popupElem) => {
    const { itemData } = refFileManager.current;
    const body = popupElem.querySelector('.e-dlg-content');
    const div = document.createElement('div');
    body.classList.add('e-dlg-body-alert');
    div.classList.add('check-container');
    const checkBox = document.createElement('input');
    checkBox.type = 'checkbox';
    checkBox.classList.add('checkbox-notify-client');
    const span = document.createElement('span');
    span.classList.add('span-notify-client');
    span.innerHTML = t('documents_notify_contact');
    body.appendChild(div);
    if (isCustomer && itemData[0].createdByContact) {
      div.appendChild(checkBox);
      div.appendChild(span);
    }
  };

  const handleErrorBody = (popupElem) => {
    const body = popupElem.querySelector('.e-dlg-content');
    body.classList.add('e-dlg-body-error');
  };

  const handleAlertFooter = (popupElem) => {
    const footer = popupElem.querySelector('.e-footer-content');
    footer.classList.add('e-dlg-footer-alert');
  };

  const handleCreateFooter = (popupElem) => {
    const footer = popupElem.querySelector('.e-footer-content');
    footer.classList.add('e-dlg-footer-create');
  };

  const handleErrorFooter = (popupElem) => {
    const footer = popupElem.querySelector('.e-footer-content');
    footer.classList.add('e-dlg-footer-error');
    const buttonName = footer.getElementsByTagName('button')[1];
    buttonName.innerHTML = t('general_button_ok');
  };

  const handlePopupOpen = (args) => {
    const popupElem = document.getElementById(args.popupModule.element.id);
    if (args.name === 'popupOpen' && args.popupName === 'Create Folder') {
      handleTitle(popupElem, t('documents_new_folder'));
      handleCreateFolder(args, popupElem);
      handleCreateFooter(popupElem);
    }
    if (args.name === 'popupOpen' && args.popupName === 'Rename') {
      handleTitle(popupElem, t('documents_rename'));
    }
    if (args.name === 'popupOpen' && args.popupName === 'Delete') {
      handleAlertHeader(popupElem);
      handleAlertBody(popupElem);
      handleAlertFooter(popupElem);
    }
    if (args.name === 'popupOpen' && args.popupName === 'Error') {
      handleErrorHeader(popupElem);
      handleErrorBody(popupElem);
      handleAlertFooter(popupElem);
    }
    if (args.name === 'popupOpen' && args.popupName === 'Extension Change') {
      handleErrorHeader(popupElem);
      handleErrorBody(popupElem);
      handleErrorFooter(popupElem);
    }
  };

  const applyIconCSS = (args) => {
    for (let i = 0; i < args.items.length; i += 1) {
      const item = args.items[i];
      if (item.id === 'file_cm_paste') {
        item.iconCss = 'lf-icon-paste';
      }
      if (item.id === 'file_cm_newfolder') {
        item.iconCss = 'lf-icon-folder';
      }
      if (item.id === 'file_cm_cut') {
        item.iconCss = 'lf-icon-cut';
      }
      if (item.id === 'file_cm_copy') {
        item.iconCss = 'lf-icon-copy';
      }
      if (item.id === 'file_cm_rename') {
        item.iconCss = 'lf-icon-rename';
      }
      if (item.id === 'file_cm_delete') {
        item.iconCss = 'lf-icon-trash';
      }
      if (item.id === 'file_cm_download') {
        item.iconCss = 'lf-icon-download';
      }
      if (item.id === 'file_cm_open') {
        item.iconCss = 'lf-icon-open-window';
      }
      if (item.id === 'file_cm_new_word') {
        item.text = t('documents_new_word');
        item.iconCss = 'lf-icon-document';
      }
      if (item.id === 'file_cm_upload') {
        item.text = t('documents_upload_document');
        item.iconCss = 'lf-icon-up-file';
      }
      if (item.id === 'file_cm_create_notification') {
        if (refFileManager.current.itemData[0].alerts.length > 0) {
          item.text = t('notification_edit_alert');
        } else {
          item.text = t('notification_create_alert');
        }
        item.iconCss = 'lf-icon-notification';
      }
      if (item.id === 'file_cm_create_communication') {
        item.text = t('notification_communication_create');
        item.iconCss = 'lf-icon-conversation';
      }
      if (item.id === 'file_cm_document_share_permission') {
        item.text = t('documents_share_permission');
        item.iconCss = 'lf-icon-share';
      }
      if (item.id === 'file_cm_un_public_document') {
        item.text = t('document_un_public_title');
        item.iconCss = 'lf-icon-invisible-document';
      }
      if (item.id === 'file_cm_property') {
        item.text = t('notification_property');
        item.iconCss = 'lf-icon-configuration';
      }
    }
  };

  const handleMenuOpenClient = (args) => {
    const fileManager = refFileManager;
    if (fileManager.current.selectedItems.length >= 2) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...fileManager.current.contextmenuModule.disabledItems,
        'property',
        'create_notification',
        'create_communication',
      ];
    }

    if (
      args.menuType === 'folder' &&
      !fileManager.current.contextmenuModule.disabledItems.includes('Open')
    ) {
      fileManager.current.contextmenuModule.currentItems.shift();
    }

    if (
      refFileManager.current.selectedItems.length === 1 &&
      args.fileDetails[0].readers.length === 0
    ) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...refFileManager.current.contextmenuModule.disabledItems,
        'un_public_document',
      ];
    }

    if (args.fileDetails[0].blocked) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...refFileManager.current.contextmenuModule.disabledItems,
        'Rename',
        'Delete',
        'Cut',
        'Copy',
      ];
    }

    if (args.fileDetails[0].id === '1') {
      fileManager.current.contextmenuModule.disabledItems = [
        ...refFileManager.current.contextmenuModule.disabledItems,
        'property',
      ];
    }

    if (!isCustomer) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...refFileManager.current.contextmenuModule.disabledItems,
        'create_notification',
        'create_communication',
        'document_share_permission',
        'un_public_document',
        'NewFolder',
      ];
    }
    // this prevent the rename of "Comunicaciones Portal", "Individuals" and "Masivas" folders
    if (['1/3/', '1/3/4/', '1/3/5/'].includes(args.fileDetails[0].pathId)) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...fileManager.current.contextmenuModule.disabledItems,
        'Rename',
      ];
    }
    if (
      !args.fileDetails[0].isFile &&
      (args.fileDetails[0].pathId.startsWith('1/3/') ||
        args.fileDetails[0].pathId.startsWith('1/3/4/') ||
        args.fileDetails[0].pathId.startsWith('1/3/5/'))
    ) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...fileManager.current.contextmenuModule.disabledItems,
        'Cut',
        'Paste',
      ];
    }
    applyIconCSS(args);
  };

  const handleMenuOpenRoot = (args) => {
    const fileManager = refFileManager;
    if (fileManager.current.selectedItems.length >= 2) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...fileManager.current.contextmenuModule.disabledItems,
        'property',
        'create_notification',
        'create_communication',
      ];
    }

    if (
      args.menuType === 'folder' &&
      !fileManager.current.contextmenuModule.disabledItems.includes('Open')
    ) {
      fileManager.current.contextmenuModule.currentItems.shift();
    }

    if (args.fileDetails[0].id === '1') {
      fileManager.current.contextmenuModule.disabledItems = [
        ...refFileManager.current.contextmenuModule.disabledItems,
        'property',
      ];
    }

    if (!isCustomer) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...fileManager.current.contextmenuModule.disabledItems,
        'create_notification',
        'create_communication',
        'property',
        'document_share_permission',
        'un_public_document',
        'NewFolder',
        'Rename',
        'Delete',
      ];
    }

    if (
      fileManager.current.selectedItems.length === 1 &&
      args.fileDetails[0].readers.length === 0
    ) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...fileManager.current.contextmenuModule.disabledItems,
        'un_public_document',
      ];
    }

    if (args.fileDetails[0].blocked) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...fileManager.current.contextmenuModule.disabledItems,
        'Rename',
        'Delete',
        'Cut',
        'Copy',
      ];
    }

    if (args.fileDetails[0].permission && !args.fileDetails[0].permission.copy) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...fileManager.current.contextmenuModule.disabledItems,
        'Copy',
        'Cut',
      ];
    }
    if (args.fileDetails[0].permission && !args.fileDetails[0].permission.write) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...fileManager.current.contextmenuModule.disabledItems,
        'Rename',
        'Delete',
        'new_word',
        'upload',
        'property',
      ];
    }
    // this prevent the rename of "Comunicaciones Portal", "Individuals" and "Masivas" folders
    if (['1/3/', '1/3/4/', '1/3/5/'].includes(args.fileDetails[0].pathId)) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...fileManager.current.contextmenuModule.disabledItems,
        'Rename',
      ];
    }
    if (
      !args.fileDetails[0].isFile &&
      (args.fileDetails[0].pathId.startsWith('1/3/') ||
        args.fileDetails[0].pathId.startsWith('1/3/4/') ||
        args.fileDetails[0].pathId.startsWith('1/3/5/'))
    ) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...fileManager.current.contextmenuModule.disabledItems,
        'Cut',
        'Paste',
      ];
    }
    if (
      ['1/3/', '1/3/4/', '1/3/5/'].includes(args.fileDetails[0].pathId) &&
      config.saveDocsComunications
    ) {
      fileManager.current.contextmenuModule.disabledItems = [
        ...fileManager.current.contextmenuModule.disabledItems,
        'Delete',
      ];
    }
    applyIconCSS(args);
  };

  const handleMenuOpen = (args) => {
    if (isClientView) {
      handleMenuOpenClient(args);
    }
    if (isRootView) {
      handleMenuOpenRoot(args);
    }
  };

  const onOkUnPublic = async () => {
    setShowConfirmUnPublic(false);
    setShowConfirmUnPublicFolder(false);
    const fileIds = refFileManager.current.itemData.map((item) => item.id);
    try {
      await unPublicDocument({
        data: fileIds,
        url: `${window.API_V1}/cli/items/${cusNavId}/${
          refFileManager.current.itemData[0].clientId
        }/${encodeURIComponent(mail)}/unPublish`,
      });
      refFileManager.current.refresh();
    } catch (error) {
      console.error('getting unPublicDocument error: ', error);
    }
  };

  const onCloseContactsModal = () => {
    setOpenContactsModal(false);
  };

  const onCancelAction = () => {
    setShowConfirmUnPublic(false);
    setShowConfirmUnPublicFolder(false);
  };

  const onCloseFormModal = () => {
    setOpenFormModal(false);
  };

  return {
    handlePopUpClose,
    handleMenuClick,
    elementEmptyTable,
    elementEmptyLargeIcon,
    handleToolbarCreate,
    handleActionAddClick,
    handleToolbarClick,
    loadingDownload,
    handlePopupOpen,
    handleMenuOpen,
    onOkUnPublic,
    loadingUnPublic,
    onCloseContactsModal,
    onCancelAction,
    onCloseFormModal,
    loadingCusConfig,
  };
}

export default useDocumentation;
